import * as React from 'react';
import { AuftragFilter, useAuftragList } from 'api/auftrag';
import { getPercent } from 'reports/utils';
import { AuftragPositionFilter, useAuftragPositionList } from 'api/auftragPosition';
import { defaultLineData, FilterValues, LineData, RahmenauftraegeSummary } from '../types';

export const useRahmenauftraegeSummary = (filterValues: FilterValues): RahmenauftraegeSummary => {
    const auftragListFilter = React.useMemo<AuftragFilter>(
        () => ({
            datumVon: filterValues.datumVon,
            datumBis: filterValues.datumBis,
            betreuerExact: filterValues.betreuer,
            telBetreuerExact: filterValues.telBetreuer,
            status: filterValues.status,
            rahmen: true,
            withKunde: true,
        }),
        [
            filterValues.datumVon,
            filterValues.datumBis,
            filterValues.betreuer,
            filterValues.telBetreuer,
            filterValues.status,
        ],
    );

    const auftragList = useAuftragList(auftragListFilter);

    const auftragPositionListFilter = React.useMemo<AuftragPositionFilter>(
        () => ({
            excludeReclay: filterValues.excludeReclay,
            auNrList: (auftragList.data || []).map((auftrag) => auftrag.nr),
        }),
        [auftragList.data, filterValues.excludeReclay],
    );

    const auftragPositionList = useAuftragPositionList(auftragPositionListFilter);

    const abrufAuftragListFilter = React.useMemo<AuftragFilter>(
        () => ({
            abrufZuList: (auftragList.data || []).map((auftrag) => auftrag.nr),
        }),
        [auftragList.data],
    );

    const abrufAuftragList = useAuftragList(abrufAuftragListFilter);

    const abrufAuftragPositionListFilter = React.useMemo<AuftragPositionFilter>(
        () => ({
            auNrList: (abrufAuftragList.data || []).map((auftrag) => auftrag.nr),
        }),
        [abrufAuftragList.data],
    );

    const abrufAuftragPositionList = useAuftragPositionList(abrufAuftragPositionListFilter);

    return React.useMemo<RahmenauftraegeSummary>(() => {
        const total: LineData = { ...defaultLineData };
        const betreuerTotal: { [key: number]: LineData } = {};
        const telBetreuerTotal: { [key: number]: LineData } = {};

        const lineDataList = (auftragList.data || []).map((auftrag) => {
            const auftragPositionen = (auftragPositionList.data || []).filter(
                (position) => position.auNr === auftrag.nr,
            );

            const abrufAuftraege = (abrufAuftragList.data || [])
                .filter((abrufAuftrag) => abrufAuftrag.abrufZu === auftrag.nr)
                .sort((a, b) => a.datum.localeCompare(b.datum));

            const abrufAuftragPositionen = (abrufAuftragPositionList.data || []).filter((position) =>
                abrufAuftraege.find((abrufAuftrag) => abrufAuftrag.nr === position.auNr),
            );

            // const skontoP = skontodefinitionenByArt[auftrag.zahlBed]?.skP1 || 0;

            let ekNettoRahmen = 0;
            let ekNettoAbruf = 0;
            let vkNettoRahmen = 0;
            let vkNettoAbruf = 0;

            auftragPositionen.forEach((auftragPosition) => {
                ekNettoRahmen += auftragPosition.posEkPreis * auftragPosition.posMenge;
                vkNettoRahmen += auftragPosition.posEPreis * auftragPosition.posMenge;

                if (filterValues.berechnungsart === 'offen') {
                    ekNettoAbruf += auftragPosition.posEkPreis * (auftragPosition.posMenge - auftragPosition.offen);
                    vkNettoAbruf += auftragPosition.posEPreis * (auftragPosition.posMenge - auftragPosition.offen);
                }
            });

            const bMargeRahmen = vkNettoRahmen - ekNettoRahmen;

            if (filterValues.berechnungsart === 'abrufe') {
                abrufAuftragPositionen.forEach((auftragPosition) => {
                    ekNettoAbruf += auftragPosition.posEkPreis * auftragPosition.posMenge;
                    vkNettoAbruf += auftragPosition.posEPreis * auftragPosition.posMenge;
                });
            }

            const bMargeAbruf = vkNettoAbruf - ekNettoAbruf;

            const ekNettoOffen = ekNettoRahmen - ekNettoAbruf;
            const vkNettoOffen = vkNettoRahmen - vkNettoAbruf;
            const bMargeOffen = bMargeRahmen - bMargeAbruf;

            const ekNettoAbrufP = getPercent(ekNettoRahmen, ekNettoAbruf);
            const ekNettoOffenP = getPercent(ekNettoRahmen, ekNettoOffen);
            const vkNettoAbrufP = getPercent(vkNettoRahmen, vkNettoAbruf);
            const vkNettoOffenP = getPercent(vkNettoRahmen, vkNettoOffen);
            const bMargeAbrufP = getPercent(bMargeRahmen, bMargeAbruf);
            const bMargeOffenP = getPercent(bMargeRahmen, bMargeOffen);

            total.count += 1;
            total.ekNettoRahmen += ekNettoRahmen;
            total.ekNettoAbruf += ekNettoAbruf;
            total.ekNettoOffen += ekNettoOffen;
            total.ekNettoAbrufP = getPercent(total.ekNettoRahmen, total.ekNettoAbruf);
            total.ekNettoOffenP = getPercent(total.ekNettoRahmen, total.ekNettoOffen);
            total.vkNettoRahmen += vkNettoRahmen;
            total.vkNettoAbruf += vkNettoAbruf;
            total.vkNettoOffen += vkNettoOffen;
            total.vkNettoAbrufP = getPercent(total.vkNettoRahmen, total.vkNettoAbruf);
            total.vkNettoOffenP = getPercent(total.vkNettoRahmen, total.vkNettoOffen);
            total.bMargeRahmen += bMargeRahmen;
            total.bMargeAbruf += bMargeAbruf;
            total.bMargeOffen += bMargeOffen;
            total.bMargeAbrufP = getPercent(total.bMargeRahmen, total.bMargeAbruf);
            total.bMargeOffenP = getPercent(total.bMargeRahmen, total.bMargeOffen);
            total.anzahlAbrufe += abrufAuftraege.length;

            if (auftrag.betreuer) {
                if (!betreuerTotal[auftrag.betreuer]) {
                    betreuerTotal[auftrag.betreuer] = { ...defaultLineData };
                }

                betreuerTotal[auftrag.betreuer].count += 1;

                betreuerTotal[auftrag.betreuer].ekNettoRahmen += ekNettoRahmen;
                betreuerTotal[auftrag.betreuer].ekNettoAbruf += ekNettoAbruf;
                betreuerTotal[auftrag.betreuer].ekNettoOffen += ekNettoOffen;
                betreuerTotal[auftrag.betreuer].ekNettoAbrufP = getPercent(
                    betreuerTotal[auftrag.betreuer].ekNettoRahmen,
                    betreuerTotal[auftrag.betreuer].ekNettoAbruf,
                );
                betreuerTotal[auftrag.betreuer].ekNettoOffenP = getPercent(
                    betreuerTotal[auftrag.betreuer].ekNettoRahmen,
                    betreuerTotal[auftrag.betreuer].ekNettoOffen,
                );
                betreuerTotal[auftrag.betreuer].vkNettoRahmen += vkNettoRahmen;
                betreuerTotal[auftrag.betreuer].vkNettoAbruf += vkNettoAbruf;
                betreuerTotal[auftrag.betreuer].vkNettoOffen += vkNettoOffen;
                betreuerTotal[auftrag.betreuer].vkNettoAbrufP = getPercent(
                    betreuerTotal[auftrag.betreuer].vkNettoRahmen,
                    betreuerTotal[auftrag.betreuer].vkNettoAbruf,
                );
                betreuerTotal[auftrag.betreuer].vkNettoOffenP = getPercent(
                    betreuerTotal[auftrag.betreuer].vkNettoRahmen,
                    betreuerTotal[auftrag.betreuer].vkNettoOffen,
                );
                betreuerTotal[auftrag.betreuer].bMargeRahmen += bMargeRahmen;
                betreuerTotal[auftrag.betreuer].bMargeAbruf += bMargeAbruf;
                betreuerTotal[auftrag.betreuer].bMargeOffen += bMargeOffen;
                betreuerTotal[auftrag.betreuer].bMargeAbrufP = getPercent(
                    betreuerTotal[auftrag.betreuer].bMargeRahmen,
                    betreuerTotal[auftrag.betreuer].bMargeAbruf,
                );
                betreuerTotal[auftrag.betreuer].bMargeOffenP = getPercent(
                    betreuerTotal[auftrag.betreuer].bMargeRahmen,
                    betreuerTotal[auftrag.betreuer].bMargeOffen,
                );
                betreuerTotal[auftrag.betreuer].anzahlAbrufe += abrufAuftraege.length;
            }

            if (auftrag.telBetreuer) {
                if (!telBetreuerTotal[auftrag.telBetreuer]) {
                    telBetreuerTotal[auftrag.telBetreuer] = { ...defaultLineData };
                }

                telBetreuerTotal[auftrag.telBetreuer].count += 1;
                telBetreuerTotal[auftrag.telBetreuer].count += 1;

                telBetreuerTotal[auftrag.telBetreuer].ekNettoRahmen += ekNettoRahmen;
                telBetreuerTotal[auftrag.telBetreuer].ekNettoAbruf += ekNettoAbruf;
                telBetreuerTotal[auftrag.telBetreuer].ekNettoOffen += ekNettoOffen;
                telBetreuerTotal[auftrag.telBetreuer].ekNettoAbrufP = getPercent(
                    telBetreuerTotal[auftrag.telBetreuer].ekNettoRahmen,
                    telBetreuerTotal[auftrag.telBetreuer].ekNettoAbruf,
                );
                telBetreuerTotal[auftrag.telBetreuer].ekNettoOffenP = getPercent(
                    telBetreuerTotal[auftrag.telBetreuer].ekNettoRahmen,
                    telBetreuerTotal[auftrag.telBetreuer].ekNettoOffen,
                );
                telBetreuerTotal[auftrag.telBetreuer].vkNettoRahmen += vkNettoRahmen;
                telBetreuerTotal[auftrag.telBetreuer].vkNettoAbruf += vkNettoAbruf;
                telBetreuerTotal[auftrag.telBetreuer].vkNettoOffen += vkNettoOffen;
                telBetreuerTotal[auftrag.telBetreuer].vkNettoAbrufP = getPercent(
                    telBetreuerTotal[auftrag.telBetreuer].vkNettoRahmen,
                    telBetreuerTotal[auftrag.telBetreuer].vkNettoAbruf,
                );
                telBetreuerTotal[auftrag.telBetreuer].vkNettoOffenP = getPercent(
                    telBetreuerTotal[auftrag.telBetreuer].vkNettoRahmen,
                    telBetreuerTotal[auftrag.telBetreuer].vkNettoOffen,
                );
                telBetreuerTotal[auftrag.telBetreuer].bMargeRahmen += bMargeRahmen;
                telBetreuerTotal[auftrag.telBetreuer].bMargeAbruf += bMargeAbruf;
                telBetreuerTotal[auftrag.telBetreuer].bMargeOffen += bMargeOffen;
                telBetreuerTotal[auftrag.telBetreuer].bMargeAbrufP = getPercent(
                    telBetreuerTotal[auftrag.telBetreuer].bMargeRahmen,
                    telBetreuerTotal[auftrag.telBetreuer].bMargeAbruf,
                );
                telBetreuerTotal[auftrag.telBetreuer].bMargeOffenP = getPercent(
                    telBetreuerTotal[auftrag.telBetreuer].bMargeRahmen,
                    telBetreuerTotal[auftrag.telBetreuer].bMargeOffen,
                );
                telBetreuerTotal[auftrag.telBetreuer].anzahlAbrufe += abrufAuftraege.length;
            }

            return {
                auNr: auftrag.nr,
                status: auftrag.status,
                datum: auftrag.datum,
                datBis: auftrag.datBis,
                kNr: auftrag.kNr || 0,
                text: auftrag.kunde?.name1 || '?',
                count: 0,
                ekNettoRahmen,
                ekNettoAbruf,
                ekNettoAbrufP,
                ekNettoOffen,
                ekNettoOffenP,
                vkNettoRahmen,
                vkNettoAbruf,
                vkNettoAbrufP,
                vkNettoOffen,
                vkNettoOffenP,
                bMargeRahmen,
                bMargeAbruf,
                bMargeAbrufP,
                bMargeOffen,
                bMargeOffenP,
                anzahlAbrufe: abrufAuftraege.length,
                letzterAbruf: abrufAuftraege.length > 0 ? abrufAuftraege[abrufAuftraege.length - 1].datum : '',
            };
        });

        return {
            loading: auftragList.isLoading || abrufAuftragList.isLoading,
            total,
            lineDataList,
            betreuerTotal,
            telBetreuerTotal,
        };
    }, [
        auftragList.data,
        auftragList.isLoading,
        abrufAuftragList.isLoading,
        abrufAuftragList.data,
        auftragPositionList.data,
        abrufAuftragPositionList.data,
        filterValues.berechnungsart,
    ]);
};
